import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { categoryName, searchArticles } from "@/utils/articles";

interface SearchOverlayProps {
  open: boolean;
  onClose: () => void;
}

export default function SearchOverlay({ open, onClose }: SearchOverlayProps) {
  const [keyword, setKeyword] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setKeyword("");
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const suggestions = keyword.trim() ? searchArticles(keyword).slice(0, 5) : [];

  const go = () => {
    const kw = keyword.trim();
    onClose();
    navigate(kw ? `/search?q=${encodeURIComponent(kw)}` : "/search");
  };

  return (
    <div className="fixed inset-0 z-[60]">
      <div
        className="absolute inset-0 bg-foreground-950/40 animate-fade-in"
        onClick={onClose}
      />
      <div className="relative bg-background-50 border-b border-background-200 animate-fade-up">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="flex items-center gap-3 border-b-2 border-primary-500 pb-3">
            <i className="ri-search-line text-2xl text-foreground-400 w-8 h-8 flex items-center justify-center" />
            <input
              ref={inputRef}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && go()}
              placeholder="搜索文章标题、作者或内容关键词…"
              className="flex-1 bg-transparent text-base md:text-lg text-foreground-950 placeholder:text-foreground-400 outline-none"
            />
            <button
              onClick={go}
              className="px-4 md:px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              搜索
            </button>
            <button
              onClick={onClose}
              aria-label="关闭搜索"
              className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-200 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          {suggestions.length > 0 && (
            <ul className="mt-5 space-y-1">
              <li className="text-xs uppercase tracking-widest text-foreground-400 mb-2">
                相关文章
              </li>
              {suggestions.map((a) => (
                <li key={a.id}>
                  <Link
                    to={`/detail/${a.id}`}
                    onClick={onClose}
                    className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-background-100 transition-colors"
                  >
                    <i className="ri-arrow-right-s-line text-secondary-500 w-5 h-5 flex items-center justify-center" />
                    <span className="text-sm text-foreground-800 line-clamp-1">
                      {a.title}
                    </span>
                    <span className="ml-auto hidden md:inline text-xs text-foreground-400 whitespace-nowrap">
                      {categoryName(a.category)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}